import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { loginPatientPortal, type PatientSession } from '../../api/patientPortal/auth.ts';
import { ApiError, isContractMismatch } from '../../api/client.ts';
import { patientPortalErrorMessage } from '../../api/patientPortal/errors.ts';
import { LogoLockup } from '../../components/Logo.tsx';
import { Button, ErrorText, Field } from '../../components/primitives.tsx';
import { ThemeMenu, ThemeRejectionNotice } from '../../theme/ThemeMenu.tsx';
import { PortalHelpLink } from '../help/PortalHelpLink.tsx';
import { PATIENT_SESSION_QUERY_KEY } from '../session/PatientSessionContext.ts';
import { resolvePortalRedirectTarget } from '../session/portalRedirectTarget.ts';

/**
 * Patient sign-in — the patient-portal mirror of `routes/LoginPage.tsx`, own module per this
 * directory's standing "own cookie, own error vocabulary" reasoning.
 *
 * **Handles the second-factor step inline, unlike the staff login page**, which has no MFA UI
 * anywhere in this app yet (`server/src/auth/routes.ts`'s own login never surfaces one to a
 * client). The patient surface needs one from day one: `docs/decisions/patient-portal-scope.md`
 * makes MFA optional-but-available for patients, and this page's own account-settings sibling
 * (`PortalAccountPage.tsx`) is the only way to turn it on — a patient who enrolls and then could
 * never complete a second login would be locked out by this app's own missing UI, not by anything
 * the server refuses. A `mfa_required` (409) response reveals a second step (TOTP code or a
 * recovery code) and resubmits the same email/password alongside whichever the patient supplies.
 */
export function PortalLoginPage() {
  const { t } = useTranslation('portalAuth');
  const { orgId } = useParams<{ orgId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [needsSecondFactor, setNeedsSecondFactor] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  const routerState: unknown = location.state;
  const fromState =
    typeof routerState === 'object' && routerState !== null && 'from' in routerState
      ? routerState.from
      : undefined;

  const signIn = useMutation({
    mutationFn: loginPatientPortal,
    onSuccess: (session: PatientSession) => {
      queryClient.setQueryData(PATIENT_SESSION_QUERY_KEY, session);
      if (orgId === undefined) return;
      const target = resolvePortalRedirectTarget(orgId, {
        fromState,
        fromQuery: searchParams.get('next'),
      });
      void navigate(target, { replace: true });
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.code === 'mfa_required') {
        setNeedsSecondFactor(true);
      }
    },
  });

  if (orgId === undefined) {
    return <Navigate to="/login" replace />;
  }

  const existing = queryClient.getQueryData<PatientSession>(PATIENT_SESSION_QUERY_KEY);
  if (existing !== undefined && !signIn.isPending) {
    const target = resolvePortalRedirectTarget(orgId, {
      fromState,
      fromQuery: searchParams.get('next'),
    });
    return <Navigate to={target} replace />;
  }

  return (
    <main id="main" className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center p-4">
      <div className="mb-6 flex items-center justify-between">
        <LogoLockup size={40} />
        <ThemeMenu />
      </div>
      <ThemeRejectionNotice />

      <div className="rounded-panel border border-line bg-surface p-4 shadow-panel sm:p-6">
        <div className="mb-1 flex items-center gap-2">
          <h1 className="m-0 text-xl font-semibold text-ink">{t('login.heading')}</h1>
          <PortalHelpLink topicId="signing-in-and-your-session" />
        </div>
        <p className="mt-0 mb-5 text-sm text-ink-muted">{t('login.subtitle')}</p>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            signIn.mutate({
              orgId,
              email,
              password,
              ...(needsSecondFactor && useRecoveryCode ? { recoveryCode } : {}),
              ...(needsSecondFactor && !useRecoveryCode ? { totpCode } : {}),
            });
          }}
          className="flex flex-col gap-4"
        >
          <Field
            label={t('login.emailLabel')}
            type="email"
            name="email"
            required
            autoComplete="username"
            disabled={needsSecondFactor}
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
          <Field
            label={t('login.passwordLabel')}
            type="password"
            name="password"
            required
            autoComplete="current-password"
            disabled={needsSecondFactor}
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
            }}
          />

          {needsSecondFactor ? (
            <>
              <p className="m-0 text-sm text-ink">{t('login.mfaPrompt')}</p>
              {useRecoveryCode ? (
                <Field
                  label={t('login.recoveryCodeLabel')}
                  name="recoveryCode"
                  required
                  autoComplete="off"
                  value={recoveryCode}
                  onChange={(event) => {
                    setRecoveryCode(event.target.value);
                  }}
                />
              ) : (
                <Field
                  label={t('login.totpCodeLabel')}
                  name="totpCode"
                  required
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={totpCode}
                  onChange={(event) => {
                    setTotpCode(event.target.value);
                  }}
                />
              )}
              <button
                type="button"
                className="self-start text-sm font-medium text-accent-text underline"
                onClick={() => {
                  setUseRecoveryCode((current) => !current);
                }}
              >
                {useRecoveryCode ? t('login.useTotpInstead') : t('login.useRecoveryCodeInstead')}
              </button>
            </>
          ) : null}

          {signIn.isError ? (
            isContractMismatch(signIn.error) ? (
              <div role="alert" className="rounded-control border border-danger p-3 text-sm">
                <p className="m-0 font-semibold text-ink">{t('login.contractMismatch.title')}</p>
                <p className="m-0 mt-1 text-ink">{patientPortalErrorMessage(signIn.error)}</p>
              </div>
            ) : signIn.error instanceof ApiError && signIn.error.code === 'mfa_required' ? null : (
              <ErrorText message={patientPortalErrorMessage(signIn.error)} />
            )
          ) : null}

          <Button type="submit" pending={signIn.isPending} className="w-full">
            {signIn.isPending ? t('login.submitting') : t('login.submit')}
          </Button>
        </form>

        <p className="mt-4 mb-0 text-sm text-ink-muted">
          {t('login.noAccount')}{' '}
          <Link to={`/portal/${orgId}/register`} className="font-medium text-accent-text">
            {t('login.registerLink')}
          </Link>
        </p>
      </div>
    </main>
  );
}
